/**
 * @features/property/cache.js
 * --------------------------
 * Tiny in-memory cache for the property search hook. Lives for the lifetime
 * of the SPA session — flushes on full reload (intentionally, so users
 * always see fresh data on hard refresh). Capped at 32 entries; eviction
 * is LRU.
 *
 * Design choices:
 *   • In-memory `Map` — no IndexedDB ceremony, no localStorage I/O on every
 *     page flick.
 *   • TTL'd to 60 s — fresh enough that a user filtering, then changing
 *     filter, then changing back doesn't trigger 2 round-trips, but stale
 *     entries don't survive long enough to confuse a fresh visit.
 *   • LRU because property listings rarely scroll back past the same
 *     intermediate states; the cache should favour recent queries.
 *
 * v2.71.26 — Initial extraction.
 */

const TTL_MS = 60_000;       // 60 s
const MAX_ENTRIES = 32;

class LruCache {
  constructor(max = MAX_ENTRIES, ttl = TTL_MS) {
    this.max = max;
    this.ttl = ttl;
    this.store = new Map(); // key → { value, expires }
  }

  get(key) {
    const entry = this.store.get(key);
    if (!entry) return null;
    if (entry.expires < Date.now()) {
      this.store.delete(key);
      return null;
    }
    // Mark as recently used by re-inserting (Map preserves insertion order).
    this.store.delete(key);
    this.store.set(key, entry);
    return entry.value;
  }

  set(key, value) {
    if (this.store.size >= this.max) {
      // Evict oldest.
      const oldest = this.store.keys().next().value;
      if (oldest) this.store.delete(oldest);
    }
    this.store.set(key, { value, expires: Date.now() + this.ttl });
  }

  /** Drop any entries whose key starts with the given namespace. */
  invalidatePrefix(prefix) {
    for (const key of this.store.keys()) {
      if (key.startsWith(prefix + "|")) this.store.delete(key);
    }
  }

  clear() {
    this.store.clear();
  }
}

/** Stable cache key from a namespace + a params object. */
export function cacheKey(namespace, params = {}) {
  // Sort keys so {a:1,b:2} and {b:2,a:1} share a single key.
  const sorted = Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== "")
    .sort()
    .map((k) => `${k}=${encodeURIComponent(params[k])}`)
    .join("&");
  return `${namespace}|${sorted}`;
}

export const propertyCache = new LruCache();
